import React, {
  useState,
  useMemo,
  useCallback,
  useEffect,
  useContext,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import { Link, useHistory } from 'react-router-dom';
import useSearchParams from '../../customHooks/useSearchParams';
import {
  Row,
  Col,
  Table,
  Select,
  Tooltip,
  Typography,
  Button,
  Progress,
  Input,
} from 'antd';
import {
  EditOutlined,
  ExclamationCircleOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { SimpleDialog } from '@ohif/ui/src/components/simpleDialog';
import { servicesManager } from '../../App.js';

import * as api from './api';
import './index.less';
let timer = null; //定时器 定时刷新列表
let searchInput; //....
let isChange = false;
const MarkTaskContext = React.createContext({
  id: null,
  userList: {},
  disabled: false,
  type: null,
  selectedRowKeys: [],
});

const Action = (text, record) => {
  const markTaskContext = useContext(MarkTaskContext);
  const route = {
    pathname: `/viewer/${record.studyId}`,
    search: `?pro=${markTaskContext.id}&markId=${record.id}&aiCheckId=${record.imageDataScienceCheckId}&type=${markTaskContext.type}`,
  };
  return (
    <>
      <Link to={route}>
        <EditOutlined />
      </Link>
    </>
  );
};

const apply = (text, record) => {
  return !record.applyRemark ? (
    text
  ) : (
    <Tooltip
      placement="topLeft"
      title={
        <>
          {['30', '35'].includes(record.applyStatusCode) &&
            record.tagListReject && (
              <div>
                驳回:
                {record.tagListReject.map(item => item.userName).join(',')}
              </div>
            )}
          <div>说明: {record.applyRemark}</div>
        </>
      }
      arrowPointAtCenter
    >
      <Typography.Text
        type={
          ['30', '35'].includes(record.applyStatusCode) ? 'danger' : 'success'
        }
      >
        {text}&nbsp;
        <ExclamationCircleOutlined />
      </Typography.Text>
    </Tooltip>
  );
};

const columns = [
  { title: '数据', key: 'id', dataIndex: 'checkId', width: 200 },
  {
    title: '标注者',
    dataIndex: 'tagListBy',
    editable: true,
    // filterMode: 'tree',
    filterSearch: true,
    filters: [],
  },
  {
    title: '标注状态',
    dataIndex: 'tagStatus',
    width: 120,
    filterMode: 'tree',
    // filterSearch: true,
    filters: [],
  },
  {
    title: '审核者',
    dataIndex: 'checkListBy',
    editable: true,
    filterSearch: true,
    filters: [],
  },
  {
    title: '审核状态',
    dataIndex: 'applyStatus',
    width: 120,
    render: apply,
    filterMode: 'tree',
    // filterSearch: true,
    filters: [],
  },
  {
    title: '上传者',
    dataIndex: 'chargeBy',
    ellipsis: true,
    width: 150,
    filterSearch: true,
    filters: [],
  },
  { title: '操作', key: 'action', render: Action },
  // { title: 'AI辅助标注', key: 'aiMark', render: AIMark },
];

// 编辑切换单元格
const EditableCell = ({
  title,
  editable,
  dataIndex,
  record,
  children,
  onChange,
  ...rest
}) => {
  const markTaskContext = useContext(MarkTaskContext);
  const isEdit = useMemo(
    () => ['tagListBy', 'checkListBy'].includes(dataIndex),
    [dataIndex]
  );
  const className = useMemo(
    () => (editable && !markTaskContext.disabled && isEdit ? ' editCell' : ''),
    [editable, isEdit, markTaskContext.disabled]
  );

  const [editState, setEditable] = useState(false);
  const onEdit = () =>
    editable && !markTaskContext.disabled && setEditable(true);

  // 用户值 = null: 未操作, undefined: 删除, id: 添加
  const [users, setUsers] = useState([]);
  // 分配
  const handleUpdateMarkDistribution = useCallback(
    data => {
      // 取消
      if (users === null && data) return setEditable(false);
      let workerIds = data ? users : [];
      if (!Array.isArray(workerIds)) workerIds = [workerIds];
      const scienceCheckIds = markTaskContext.selectedRowKeys.includes(
        record.id
      )
        ? [...markTaskContext.selectedRowKeys]
        : [record.id];
      api
        .updateMarkDistribution({
          scienceCheckIds,
          type: dataIndex === 'checkListBy' ? 20 : 10,
          workerIds,
        })
        .then(onChange)
        .finally(() => setEditable(false));
    },
    [dataIndex, markTaskContext, onChange, record, users]
  );

  // 聚焦选择器
  const ref = useRef(null);
  useEffect(() => {
    if (editState && ref.current) ref.current.focus();
  }, [editState, ref]);

  // 选择器使用的数据
  const selectOptions = markTaskContext.userList[dataIndex] || [];
  const data = (record && record[dataIndex]) || [];
  const defaultSelect = useMemo(() => data.map(item => item.accountId), [data]);
  const showText = useMemo(
    () => data.map(item => item.userName).join(',') || '未分配',
    [data]
  );

  return isEdit ? (
    <td
      {...rest}
      className={rest.className + className}
      title="双击进行编辑"
      onDoubleClick={onEdit}
    >
      {editState && !markTaskContext.disabled ? (
        <Select
          ref={ref}
          style={{ width: '100%' }}
          placeholder="选择一个用户"
          allowClear
          mode={dataIndex === 'tagListBy' ? 'multiple' : undefined}
          defaultValue={defaultSelect}
          options={selectOptions}
          onChange={setUsers}
          onFocus={() => setUsers(null)}
          onBlur={handleUpdateMarkDistribution}
          onClear={handleUpdateMarkDistribution}
        />
      ) : (
        showText
      )}
    </td>
  ) : (
    <td {...rest}>{children}</td>
  );
};
EditableCell.propTypes = {
  title: PropTypes.string,
  editable: PropTypes.bool,
  dataIndex: PropTypes.string,
  record: PropTypes.object,
  children: PropTypes.any,
  onChange: PropTypes.func,
};

const MarkTask = ({ id, disabled }) => {
  const [filterValues, setFilterValues] = useState({
    keyword: '', //关键字
    taggerList: [], //标注者
    tagStatus: [], //标注状态
    auditors: [], //审核者
    auditStatus: [], //审核状态
    chargeBys: [], //责任人
    tags: [], //标签
  });
  const [filterOption, setFilterOption] = useState({
    tagger: [],
    tagStatus: [],
    auditor: [],
    auditStatus: [],
    uploader: [],
    tags: [],
  });
  const [aimark, setAimark] = useState({
    aiTagId: '',
    aiTagName: '',
  });

  const [loading, setLoading] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false); //弹窗提示
  const [dialogText, setDialogText] = useState(''); //弹窗提示语
  const [imageDataScienceCheckId, setImageDataScienceCheckId] = useState(''); //当前imageDataScienceCheckId
  const [aiTagId, setAiTagId] = useState(''); //当前imageDataScienceCheckId
  const [list, setList] = useState([]);

  const [{ type }] = useSearchParams();
  const [userList, setUserList] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 5,
    total: 0,
  });
  const { current, pageSize } = pagination;
  useEffect(() => {
    if (isChange) {
      setFilterValues({
        keyword: '', //关键字
        taggerList: [], //标注者
        tagStatus: [], //标注状态
        auditors: [], //审核者
        auditStatus: [], //审核状态
        chargeBys: [], //责任人
        tags: [], //标签
      });
      // setOriginId(id);
      isChange = false;
    }
    Promise.all([
      api.queryDistributionUserList({ id, type: 10 }),
      api.queryDistributionUserList({ id, type: 20 }),
    ]).then(res => {
      const [tagListBy, checkListBy] = res.map(item =>
        Array.isArray(item.data)
          ? item.data.map(user => ({
            label: user.userName,
            value: user.accountId,
          }))
          : []
      );
      setUserList({ tagListBy: tagListBy, checkListBy: checkListBy });
    });
    // 初始化筛选条件
    const handleRequest = type
      ? api.getSelectOption({ id, type })
      : api.getDistributionSelectOption({ id });
    handleRequest.then(res => {
      let {
        tagger,
        tagStatus,
        auditor,
        auditStatus,
        uploader,
        tags,
      } = res.data;
      if (tagger.length) {
        //标注者
        tagger = toFilterArr(tagger);
      }
      if (tagStatus.length) {
        //标注状态
        tagStatus = toFilterArr(tagStatus);
      }
      if (auditor.length) {
        //审核者
        auditor = toFilterArr(auditor);
      }
      if (auditStatus.length) {
        //审核状态
        auditStatus = toFilterArr(auditStatus);
      }
      if (uploader.length) {
        //责任人
        uploader = toFilterArr(uploader);
      }
      if (tags.length) {
        //标签
        // columns[5].filters = toFilterArr(auditStatus);
      }
      setFilterOption({
        tagger,
        tagStatus,
        auditor,
        auditStatus,
        uploader,
        tags,
      });
    });
  }, [id, type]);

  const handleQueryMarkList = useCallback(
    data => {
      // 自动刷新时会出现loading 影响体验 暂先屏蔽
      // setLoading(true);
      let params = {
        id: id,
        keyword: data.keyword,
        taggerList: data.taggerList,
        tagStatus: data.tagStatus,
        auditor: data.auditor,
        auditStatus: data.auditStatus,
        chargeBys: data.chargeBys,
        tags: data.tags,
        pageNum: current - 1,
        pageSize: pageSize,
      };
      if (type) {
        params.type = type;
      }
      const handleRequest = type
        ? api.queryTaskList(params)
        : api.queryMarkDistribution(params);
      handleRequest
        .then(res => {
          // setTotal(res.data.total);
          let total = 5;
          let records = [];
          if (res.data) {
            total = res.data.total;
            records = res.data.records;
          }
          setPagination(state => ({ ...state, total: total }));
          setList(records);
        })
        .finally(() => setLoading(false));
    },
    [id, current, pageSize, type]
  );

  const test = useRef(filterValues);
  test.current = filterValues;
  useEffect(() => {
    handleQueryMarkList(test.current);
    timer = setInterval(() => {
      handleQueryMarkList(test.current);
    }, 60000);
    return () => {
      clearInterval(timer);
    };
  }, [handleQueryMarkList]);

  useEffect(() => {
    if (isChange) {
      handleQueryMarkList(filterValues);
    }
  }, [filterValues, handleQueryMarkList]);

  const AIMark = (t, e, item) => {
    setImageDataScienceCheckId(e);
    setAiTagId(item.aiTagId);
    setIsDialogOpen(true);
    if (t == '1') {
      setDialogText(`该功能将覆盖所有已标注的${item.aiTagName}数据,是否继续？`);
    }
    if (t == '2') {
      setDialogText(
        `该操作将重新预测并覆盖已预测的${item.aiTagName}数据,是否继续？`
      );
    }
  };

  //定义表头2
  const getColumnSearchProps = useCallback(
    dataIndex => ({
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm }) => (
        <div style={{ padding: 8 }}>
          <Input
            ref={node => {
              searchInput = node;
            }}
            placeholder={`请输入`}
            value={selectedKeys[0]}
            onChange={e =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            onPressEnter={() => {
              confirm();
            }}
          // suffix={<SearchOutlined style={{ color: '#1890ff' }} />}
          />
        </div>
      ),
      filterIcon: filtered => (
        <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
      ),
      onFilterDropdownVisibleChange: visible => {
        if (visible) {
          setTimeout(() => searchInput.select(), 100);
        }
      },
    }),
    []
  );

  //转换数据
  const toFilterArr = data => {
    if (!data) return [];
    if (data.length) {
      return data.map(i => {
        return {
          text: i.label,
          value: i.value,
        };
      });
    }
    return [];
  };

  // 定义表列数据
  const formatColumns = useMemo(() => {
    // columns[0].title = search;
    columns[0] = {
      title: '数据',
      key: 'id',
      dataIndex: 'checkId',
      width: 200,
      filteredValue: filterValues.keyword || null,
      ...getColumnSearchProps('checkId'),
    };
    columns[1].filters = filterOption.tagger;
    columns[1].filteredValue = filterValues.taggerList || null;
    columns[2].filters = filterOption.tagStatus;
    columns[2].filteredValue = filterValues.tagStatus || null;
    columns[3].filters = filterOption.auditor;
    columns[3].filteredValue = filterValues.auditors || null;
    columns[4].filters = filterOption.auditStatus;
    columns[4].filteredValue = filterValues.auditStatus || null;
    columns[5].filters = filterOption.uploader;
    columns[5].filteredValue = filterValues.chargeBys || null;

    let newcolumns = columns;
    if (type == '10') {
      newcolumns = columns.concat({
        title: 'AI辅助标注',
        key: 'aiMark',
        render(_, record) {
          if (record.show && record.tagAis.length) {
            let tagAis = record.tagAis;
            let defaultAiValues = tagAis[0];
            let aiValues = tagAis
              .filter(i => i.selected)
              .map(i => {
                return {
                  aiTagId: i.aiTagId,
                  aiTagName: i.aiTagName,
                  imageDataScienceAiTagId: i.imageDataScienceAiTagId,
                  progress: parseInt(i.progress),
                  lastExecute: i.lastExecute,
                  error: i.error,
                };
              });
            if (aiValues.length) {
              defaultAiValues = aiValues[0];
            } else {
              aiValues = tagAis
                .filter(i => i.lastExecute)
                .map(i => {
                  return {
                    aiTagId: i.aiTagId,
                    aiTagName: i.aiTagName,
                    imageDataScienceAiTagId: i.imageDataScienceAiTagId,
                    progress: parseInt(i.progress),
                    lastExecute: i.lastExecute,
                    error: i.error,
                  };
                });
              if (aiValues.length) {
                defaultAiValues = aiValues[0];
              } else {
                defaultAiValues.progress = parseInt(defaultAiValues.progress);
              }
            }

            if (defaultAiValues.progress) {
              let aiValue = aiValues[0];
              let color = '#6c8da9';
              if (defaultAiValues.progress == 100 && defaultAiValues.error) {
                color = 'red';
              }
              return (
                <div className="aimark">
                  <Select
                    defaultValue={defaultAiValues.aiTagName}
                    style={{
                      width: '120px',
                    }}
                    size={'small'}
                    onChange={(e, o) => {
                      record.tagAis.map(i => {
                        i.selected = false;
                        if (i.aiTagName === e) {
                          i.selected = true;
                        }
                      });
                      setAimark(o.label);
                    }}
                  >
                    {record.tagAis.map((item, index) => (
                      <Select.Option
                        key={index}
                        value={item.aiTagName}
                        label={item}
                      >
                        {item.aiTagName}
                      </Select.Option>
                    ))}
                  </Select>
                  <div className="aimark-right">
                    <Progress
                      className="aimark-progress"
                      percent={defaultAiValues.progress}
                      showInfo={false}
                      strokeColor={color}
                      trailColor={'#ddd'}
                      strokeWidth={24}
                    />
                    {aiValue.progress == 100 ? (
                      <div className="aimark-finish">完成</div>
                    ) : (
                      <div className="aimark-in">进行中</div>
                    )}
                    {aiValue.progress == 100 ? (
                      <div
                        className="aimark-btn"
                        onClick={e => {
                          AIMark(
                            '2',
                            record.imageDataScienceCheckId,
                            defaultAiValues
                          );
                          e.stopPropagation();
                        }}
                      >
                        {'重新预测'}
                      </div>
                    ) : null}
                  </div>
                </div>
              );
            } else {
              return (
                <div className="aimark">
                  <Select
                    defaultValue={defaultAiValues.aiTagName}
                    style={{
                      width: '120px',
                    }}
                    size={'small'}
                    onChange={(e, o) => {
                      record.tagAis.map(i => {
                        i.selected = false;
                        if (i.aiTagName === e) {
                          i.selected = true;
                        }
                      });
                      setAimark(o.label);
                    }}
                  >
                    {record.tagAis.map((item, index) => (
                      <Select.Option
                        key={index}
                        value={item.aiTagName}
                        label={item}
                      >
                        {item.aiTagName}
                      </Select.Option>
                    ))}
                  </Select>
                  <div className="aimark-right">
                    <div
                      className="run"
                      onClick={e => {
                        console.log(aimark);
                        AIMark(
                          '1',
                          record.imageDataScienceCheckId,
                          defaultAiValues
                        );
                        e.stopPropagation();
                      }}
                    >
                      开始执行
                    </div>
                  </div>
                </div>
              );
            }
          } else {
            return null;
          }
        },
      });
    }
    const data = newcolumns.map(item =>
      ['tagListBy', 'checkListBy'].includes(item.dataIndex)
        ? Object.assign(item, {
          onCell: record => ({
            record,
            editable: item.editable && !type,
            dataIndex: item.dataIndex,
            title: item.title,
            onChange: handleQueryMarkList,
          }),
        })
        : item
    );
    return data;
  }, [
    filterValues,
    getColumnSearchProps,
    filterOption,
    type,
    aimark,
    handleQueryMarkList,
  ]);

  //AI辅助标注
  const strAIMark = () => {
    api
      .getAiTags({
        aiTagId: aiTagId,
        imageDataScienceCheckId: imageDataScienceCheckId,
      })
      .then(res => {
        const { UINotificationService } = servicesManager.services;
        if (res.status == 'success') {
          const message = res.tip;
          setIsDialogOpen(false);
          UINotificationService.show({
            title: '提示',
            message,
            type: 'info',
            autoClose: true,
          });
          handleQueryMarkList(filterValues);
        }
      });
  };

  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  const history = useHistory();
  const [searchParams, joinSearch] = useSearchParams();
  const searchParamsRef = useRef(searchParams);
  searchParamsRef.current = searchParams;
  const genSetPagination = useCallback((current, pageSize) => {
    setSelectedRowKeys([]);
    setPagination(state => ({
      current: Number(current) || state.current,
      pageSize: Number(pageSize) || state.pageSize,
      total: state.total,
    }));
  }, []);
  // 映射分页到URL
  useEffect(() => {
    history.replace({
      search: joinSearch({
        ...searchParamsRef.current,
        markTaskPage: pagination.current,
        markTaskPageSize: pagination.pageSize,
      }),
    });
  }, [history, joinSearch, pagination]);
  // 初始化分页与选中数据
  useEffect(() => {
    if (loading) return;
    const { markTaskPage, markTaskPageSize } = searchParamsRef.current;
    genSetPagination(markTaskPage, markTaskPageSize);
  }, [genSetPagination, loading]);

  // 判断两个对象是否相同
  const isObjectValueEqual = (obj1, obj2) => {
    var aProps = Object.getOwnPropertyNames(obj1);
    var bProps = Object.getOwnPropertyNames(obj2);
    if (aProps.length != bProps.length) {
      return false;
    }
    for (var i = 0; i < aProps.length; i++) {
      var propName = aProps[i];

      var propA = obj1[propName];
      var propB = obj2[propName];
      if (typeof propA === 'object') {
        if (isObjectValueEqual(propA, propB)) {
          // return true     这里不能return ,后面的对象还没判断
        } else {
          return false;
        }
      } else if (propA !== propB) {
        return false;
      }
    }
    return true;
  };

  const tableOnChange = (pagination, filters, sorter, extra) => {
    if (extra.action === 'filter') {
      let obj = {
        keyword: filters.id ? filters.id : '', //关键字
        taggerList: filters.tagListBy ? filters.tagListBy : [], //标注者
        tagStatus: filters.tagStatus ? filters.tagStatus : [], //标注状态
        auditors: filters.checkListBy ? filters.checkListBy : [], //审核者
        auditStatus: filters.applyStatus ? filters.applyStatus : [], //审核状态
        chargeBys: filters.chargeBy ? filters.chargeBy : [], //责任人
        tags: [], //标签
      };
      if (!isObjectValueEqual(filterValues, obj)) {
        isChange = true;
        setFilterValues(obj);
      }
    }
  };
  return (
    <>
      <Row>
        {/* <Col span={12}></Col> */}
        <Col span={24}>
          <MarkTaskContext.Provider
            value={{ id, userList, disabled, type, selectedRowKeys }}
          >
            <Table
              scroll={{ x: '100%' }}
              rowKey="id"
              components={{
                body: {
                  cell: EditableCell,
                },
              }}
              columns={formatColumns}
              dataSource={list}
              loading={loading}
              pagination={{
                size: 'small',
                ...pagination,
                onChange: genSetPagination,
              }}
              onChange={tableOnChange}
              rowSelection={{ selectedRowKeys, onChange: setSelectedRowKeys }}
            />
          </MarkTaskContext.Provider>
        </Col>
      </Row>
      <SimpleDialog
        rootClass="datasetsListDialog"
        isOpen={isDialogOpen}
        headerTitle="提示信息"
        onClose={e => {
          setIsDialogOpen(false);
        }}
        onConfirm={strAIMark}
      >
        <label className="simpleDialogLabelFor">{dialogText}</label>
      </SimpleDialog>
    </>
  );
};

MarkTask.propTypes = {
  id: PropTypes.number,
  disabled: PropTypes.bool,
};

export default MarkTask;
